import {EventEmitter, inject, Injectable} from '@angular/core';
import {TutorialQuery} from '../../queries/tutorial.query';
import {TutorialStore} from '../../stores/tutorial.store';
import {MeService as ApiMeService} from '../../api/services/me.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {firstValueFrom} from 'rxjs';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class TutorialService {
    private readonly apiMeService = inject(ApiMeService);
    private readonly authService = inject(AuthService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly tutorialQuery = inject(TutorialQuery);
    private readonly tutorialStore = inject(TutorialStore);
    private lastStepsFetch: number = 0;
    private readonly stepsFetchIntervalMS: number = 1000;
    private readonly triggerTutorialStartEvent: EventEmitter<number> = new EventEmitter();

    public toggleHideTutorial(): void {
        const isTutorialHidden = this.tutorialQuery.getIsTutorialHidden();

        this.tutorialStore.update({
            isHidden: !isTutorialHidden,
        });
    }

    public triggerTutorialStart(): void {
        this.triggerTutorialStartEvent.emit(Date.now());
    }

    public async clearTutorialSteps(): Promise<void> {
        try {
            await firstValueFrom(this.apiMeService.MeDeleteDisplayedLearningHints(await this.authService.getBearer()));
            this.tutorialStore.update({ readTutorialStepIds: [] });
        } catch (error) {
            this.snackbarService.showError('ERROR.TUTORIAL.RESET_FAILED');
            console.error(error);
        }
    }

    public async fetchReadTutorialStepIds(): Promise<void> {
        const currentTimeStamp = Date.now();
        if (this.stepsFetchIntervalMS + this.lastStepsFetch > currentTimeStamp) {
            return;
        }
        this.lastStepsFetch = currentTimeStamp;
        let readTutorialStepIds = [];
        const bearer = await this.authService.getBearer();
        if (bearer) {
            try {
                const learningHints = await firstValueFrom(this.apiMeService.MeGetDisplayedLearningHints(bearer));
                const stepIds = this.tutorialQuery.getReadTutorialStepIds();
                const usedStepIds: Array<string> = [];
                readTutorialStepIds = [
                    ...stepIds,
                    ...learningHints.keys
                ];
                readTutorialStepIds = readTutorialStepIds.filter(stepId => {
                    if (!usedStepIds.includes(stepId)) {
                        usedStepIds.push(stepId);
                        return true;
                    }
                    return false;
                });
                this.tutorialStore.update({ readTutorialStepIds });
            } catch (error) {
                this.snackbarService.showError('ERROR.TUTORIAL.TUTORIAL_STEPS_LOADING_ERROR');
                console.error(error);
            }
        }
    }

    public saveCurrentProgressToStore(readTutorialStepId: string): void {
        const stepIds = this.tutorialQuery.getReadTutorialStepIds();
        if (!stepIds.includes(readTutorialStepId)) {
            this.tutorialStore.update({
                readTutorialStepIds: [
                    ...stepIds,
                    readTutorialStepId
                ]
            });
        }
    }

    public async saveCompleteProgressPermanently(): Promise<void> {
        const keys = this.tutorialQuery.getReadTutorialStepIds();
        try {
            await firstValueFrom(this.apiMeService.MeAddDisplayedLearningHints({
                data: {
                    keys
                },
                Authorization: await this.authService.getBearer()
            }));
        } catch (error) {
            this.snackbarService.showError('ERROR.TUTORIAL.SAVE_FAILED');
            console.error(error);
        }
    }

    public getTutorialStartTriggerEvent(): EventEmitter<number> {
        return this.triggerTutorialStartEvent;
    }
}
